import { Command as CommandPrimitive } from "cmdk";
import { ALargeSmall, Plus, Search } from "lucide-react";
import type { FC, ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { Kbd } from "@/components/ui/kbd";
import { Skeleton } from "@/components/ui/skeleton";
import {
  panelAgentLabel,
  type PanelAgent
} from "@/features/agents/agentPanelStore";
import { scopeKey, type AgentScope } from "@/features/agents/types";
import { useAgentPanelScope } from "@/features/agents/useAgentPanelScope";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import { TabMark } from "@/features/appTabs/components/TabMark";
import { useAppTabsStore } from "@/features/appTabs/context/appTabsStore";
import {
  groupMark,
  groupLabel,
  groupTabs,
  lastUsedTab,
  type TabGroup
} from "@/features/appTabs/groups";
import { useDashboardDraft } from "@/features/dashboard/useDashboardDraft";
import { highlightRuns } from "@/features/clientProfile/tabs/notes/notesModel";
import {
  DENSITY_LABELS,
  matchDensityCommands,
  useDensity
} from "@/features/density";
import { openNewCase } from "@/features/newCase";
import { openNewClient } from "@/features/newClient";

import type { CaseStatus } from "../../api/types/case";
import { statusColor } from "../../components/case/caseStatusColors";
import { useUserProfiles } from "../userProfiles/context/UserProfilesContext";
import { useGlobalSearchDialog } from "./GlobalSearchContext";
import { MIN_QUERY_LENGTH } from "./searchModel";
import type { SearchResult } from "./types";
import { useGlobalSearch } from "./useGlobalSearch";

const OPEN_TAB_LIMIT = 5;

const ResultAvatar: FC<{ result: SearchResult }> = ({ result }) => {
  const { get } = useUserProfiles();
  const image = result.avatarProfileId
    ? get(result.avatarProfileId)?.avatar_data_url
    : undefined;
  return (
    <Avatar size="2xs">
      {image ? <AvatarImage src={image} alt="" /> : null}
      <AvatarFallback>{result.initials}</AvatarFallback>
    </Avatar>
  );
};

const StatusDot: FC<{ status?: CaseStatus | string }> = ({ status }) => (
  <span
    aria-hidden
    className="size-2 shrink-0 rounded-full"
    style={{ backgroundColor: statusColor(status as CaseStatus).color }}
  />
);

const Row: FC<{
  value: string;
  lead: ReactNode;
  label: string;
  sublabel?: string;
  right?: ReactNode;
  onSelect: () => void;
  children?: ReactNode;
}> = ({ value, lead, label, sublabel, right, onSelect, children }) => (
  <CommandItem value={value} onSelect={onSelect} className="gap-2.5 px-2.5">
    <span className="flex size-4 shrink-0 items-center justify-center text-fg-3">
      {lead}
    </span>
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <span className="truncate text-fg">{label}</span>
      {children}
      {sublabel ? (
        <span className="min-w-0 truncate text-sm text-fg-3">{sublabel}</span>
      ) : null}
    </span>
    {right ? (
      <span
        data-slot="command-shortcut"
        className="ml-auto flex shrink-0 items-center text-sm text-fg-4"
      >
        {right}
      </span>
    ) : null}
  </CommandItem>
);

/** A note hit on two lines: the passage with the query marked, then client · author · date. */
const NoteRow: FC<{
  result: SearchResult;
  query: string;
  onSelect: () => void;
}> = ({ result, query, onSelect }) => {
  const Icon = result.icon;
  return (
    <CommandItem
      value={`${result.kind}:${result.id}`}
      onSelect={onSelect}
      className="min-h-12 items-start gap-2.5 px-2.5 py-1.5"
    >
      <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center text-fg-3">
        {Icon ? <Icon className="size-3.5" /> : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-fg">
          {highlightRuns(result.label, query).map((run, i) =>
            run.hit ? (
              <mark key={i} className="rounded-xs bg-warn-soft text-inherit">
                {run.text}
              </mark>
            ) : (
              <span key={i}>{run.text}</span>
            )
          )}
        </span>
        {result.sublabel ? (
          <span className="truncate text-sm text-fg-3">{result.sublabel}</span>
        ) : null}
      </span>
      <span
        data-slot="command-shortcut"
        className="mt-0.5 ml-auto shrink-0 text-sm text-fg-4"
      >
        Note
      </span>
    </CommandItem>
  );
};

const ResultRow: FC<{ result: SearchResult; onSelect: () => void }> = ({
  result,
  onSelect
}) => {
  const Icon = result.icon;
  return (
    <Row
      value={`${result.kind}:${result.id}`}
      onSelect={onSelect}
      lead={
        Icon ? <Icon className="size-3.5" /> : <ResultAvatar result={result} />
      }
      label={result.label}
      sublabel={result.sublabel}
      right={
        result.kind === "case" && result.status ? result.status : undefined
      }
    >
      {result.kind === "case" ? <StatusDot status={result.status} /> : null}
    </Row>
  );
};

/** "EB-1A case" for a client group's cases; nothing for a page. */
const groupSublabel = (group: TabGroup): string | undefined => {
  const visas = group.tabs
    .filter((t) => t.kind === "case" && t.workflow)
    .map((t) => t.workflow);
  if (!visas.length) return undefined;
  return visas.length === 1 ? `${visas[0]} case` : visas.join(" · ");
};

const LoadingRows: FC = () => (
  <div className="flex flex-col gap-1 px-2.5 py-1.5" aria-busy>
    {[0, 1, 2].map((i) => (
      <div key={i} className="flex items-center gap-2.5 py-1">
        <Skeleton className="size-4 rounded-full" />
        <Skeleton className="h-3.5 w-40" />
        <Skeleton className="h-3 w-24" />
      </div>
    ))}
  </div>
);

const Hint: FC<{ keys: string[]; children: ReactNode }> = ({
  keys,
  children
}) => (
  <span className="flex items-center gap-1">
    {keys.map((k) => (
      <Kbd key={k}>{k}</Kbd>
    ))}
    <span className="ml-0.5">{children}</span>
  </span>
);

const AskRow: FC<{
  agent: PanelAgent;
  scope: AgentScope;
  query: string;
  onSent: () => void;
}> = ({ agent, scope, query, onSent }) => {
  const { send, isSending } = useDashboardDraft(agent, scope);
  return (
    <Row
      value="ask-assistant"
      onSelect={() => {
        if (isSending) return;
        onSent();
        void send(query);
      }}
      lead={
        <AgentMoodAvatar
          className="size-4 rounded-full"
          agent={{ key: scopeKey(scope) }}
        />
      }
      label={`Ask ${panelAgentLabel(agent)}`}
      sublabel={query.trim()}
    />
  );
};

/**
 * Cmd+K palette: the open tabs, static page shortcuts and actions,
 * server-searched clients and cases, the firm's library documents and skills
 * (loaded once, matched here), and a hand-off of the query to the assistant.
 */
export const GlobalSearchDialog: FC = () => {
  const { open, setOpen } = useGlobalSearchDialog();
  const navigate = useNavigate();
  const search = useGlobalSearch(open, { knowledge: true });
  const { reset } = search;
  const { ensure } = useUserProfiles();
  const tabs = useAppTabsStore((s) => s.tabs);
  const recency = useAppTabsStore((s) => s.recency);
  const activeId = useAppTabsStore((s) => s.activeId);
  const { agent, scope } = useAgentPanelScope();
  const { density, setDensity } = useDensity();

  // Reset the query whenever the palette closes.
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  // Warm avatars for the visible client / case rows.
  useEffect(() => {
    const ids = [...search.clients, ...search.cases]
      .map((r) => r.avatarProfileId)
      .filter((id): id is string => Boolean(id));
    if (ids.length) ensure(ids).catch(() => undefined);
  }, [search.clients, search.cases, ensure]);

  const needle = search.query.trim().toLowerCase();
  const openTabs = useMemo(() => {
    const rank = (g: TabGroup) => {
      const i = recency.indexOf(lastUsedTab(g, recency).id);
      return i < 0 ? Number.POSITIVE_INFINITY : i;
    };
    return groupTabs(tabs)
      .filter((g) => !g.tabs.some((t) => t.id === activeId))
      .filter((g) => !needle || groupLabel(g).toLowerCase().includes(needle))
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, OPEN_TAB_LIMIT);
  }, [tabs, recency, needle, activeId]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  const destinations = search.pages.filter((p) => !p.action);
  const actions = search.pages.filter((p) => p.action);
  const hasRecords =
    search.clients.length > 0 ||
    search.cases.length > 0 ||
    search.notes.length > 0 ||
    search.library.length > 0 ||
    search.skills.length > 0;
  const densityCommands = matchDensityCommands(search.query);
  const canAsk = Boolean(agent && scope);
  const showEmpty =
    !search.isLoading &&
    !canAsk &&
    openTabs.length === 0 &&
    search.pages.length === 0 &&
    densityCommands.length === 0 &&
    !hasRecords &&
    needle.length > 0;

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Search"
      description="Search clients, cases, firm knowledge, or jump to a page"
      className="top-20 gap-0 sm:max-w-160"
    >
      {/* Results are server-filtered; disable cmdk's own matching. */}
      <Command shouldFilter={false} className="rounded-xl p-0">
        <div className="flex h-12 shrink-0 items-center gap-2.5 border-b border-line-2 px-3.5">
          <Search className="size-4 shrink-0 text-fg-3" aria-hidden />
          <CommandPrimitive.Input
            data-slot="command-input"
            placeholder="Search clients, cases, firm knowledge, or pages..."
            value={search.query}
            onValueChange={search.setQuery}
            className="h-full min-w-0 flex-1 bg-transparent text-md text-fg outline-none placeholder:text-fg-4"
          />
          <Kbd>esc</Kbd>
        </div>
        <CommandList className="max-h-120 py-1.5">
          {showEmpty ? <CommandEmpty>No results.</CommandEmpty> : null}

          {openTabs.length > 0 ? (
            <CommandGroup heading="Open Tabs" className="px-1.5 py-0">
              {openTabs.map((g) => {
                const page = g.tabs.length === 1 && g.tabs[0].kind === "page";
                return (
                  <Row
                    key={`tab:${g.key}`}
                    value={`tab:${g.key}`}
                    onSelect={() => go(lastUsedTab(g, recency).route)}
                    lead={<TabMark tab={groupMark(g)} size="sm" />}
                    label={groupLabel(g)}
                    sublabel={groupSublabel(g)}
                    right={page ? "Page" : "Client"}
                  />
                );
              })}
            </CommandGroup>
          ) : null}

          {search.pages.length > 0 ? (
            <CommandGroup heading="Jump To" className="px-1.5 py-0">
              {[...destinations, ...actions].map((r) =>
                r.id === "new-case" ? (
                  <Row
                    key={`page:${r.id}`}
                    value={`page:${r.id}`}
                    onSelect={() => {
                      setOpen(false);
                      openNewCase();
                    }}
                    lead={<Plus className="size-3.5" />}
                    label={r.label}
                  />
                ) : (
                  <ResultRow
                    key={`page:${r.id}`}
                    result={r}
                    onSelect={() => {
                      if (r.id !== "new-client") {
                        go(r.to);
                        return;
                      }
                      setOpen(false);
                      openNewClient();
                    }}
                  />
                )
              )}
            </CommandGroup>
          ) : null}

          {densityCommands.length > 0 ? (
            <CommandGroup heading="Display" className="px-1.5 py-0">
              {densityCommands.map((value) => (
                <Row
                  key={`density:${value}`}
                  value={`density:${value}`}
                  onSelect={() => {
                    setOpen(false);
                    setDensity(value);
                  }}
                  lead={<ALargeSmall className="size-3.5" />}
                  label={`Display: ${DENSITY_LABELS[value]}`}
                  right={value === density ? "Current" : undefined}
                />
              ))}
            </CommandGroup>
          ) : null}

          {search.isServerQuery ? (
            <>
              {search.isLoading && !hasRecords ? <LoadingRows /> : null}

              {search.clients.length > 0 ? (
                <CommandGroup heading="Clients" className="px-1.5 py-0">
                  {search.clients.map((r) => (
                    <ResultRow
                      key={`client:${r.id}`}
                      result={r}
                      onSelect={() => go(r.to)}
                    />
                  ))}
                </CommandGroup>
              ) : null}

              {search.cases.length > 0 ? (
                <CommandGroup heading="Cases" className="px-1.5 py-0">
                  {search.cases.map((r) => (
                    <ResultRow
                      key={`case:${r.id}`}
                      result={r}
                      onSelect={() => go(r.to)}
                    />
                  ))}
                </CommandGroup>
              ) : null}

              {search.notes.length > 0 ? (
                <CommandGroup heading="Notes" className="px-1.5 py-0">
                  {search.notes.map((r) => (
                    <NoteRow
                      key={`note:${r.id}`}
                      result={r}
                      query={search.query}
                      onSelect={() => go(r.to)}
                    />
                  ))}
                </CommandGroup>
              ) : null}

              {search.library.length > 0 ? (
                <CommandGroup heading="Firm Library" className="px-1.5 py-0">
                  {search.library.map((r) => (
                    <ResultRow
                      key={`library:${r.id}`}
                      result={r}
                      onSelect={() => go(r.to)}
                    />
                  ))}
                </CommandGroup>
              ) : null}

              {search.skills.length > 0 ? (
                <CommandGroup heading="Automations" className="px-1.5 py-0">
                  {search.skills.map((r) => (
                    <ResultRow
                      key={`skill:${r.id}`}
                      result={r}
                      onSelect={() => go(r.to)}
                    />
                  ))}
                </CommandGroup>
              ) : null}

              {search.error ? (
                <div className="px-4 py-2 text-sm text-danger">
                  {search.error}
                </div>
              ) : null}
            </>
          ) : needle.length > 0 && needle.length < MIN_QUERY_LENGTH ? (
            <div className="px-4 py-2 text-sm text-fg-3">
              Keep typing to search clients, cases and firm knowledge.
            </div>
          ) : null}

          {agent && scope && needle ? (
            <CommandGroup heading="Ask" className="px-1.5 py-0">
              <AskRow
                agent={agent}
                scope={scope}
                query={search.query}
                onSent={() => setOpen(false)}
              />
            </CommandGroup>
          ) : null}
        </CommandList>
        <div className="flex h-9 shrink-0 items-center gap-3 border-t border-line-2 px-3.5 text-sm text-fg-3">
          <Hint keys={["↑", "↓"]}>Move</Hint>
          <Hint keys={["↵"]}>Open</Hint>
          {agent && scope ? (
            <span className="ml-auto flex min-w-0 items-center gap-1.5">
              <AgentMoodAvatar
                className="size-3.5 shrink-0 rounded-full"
                agent={{ key: scopeKey(scope) }}
              />
              <span className="truncate">
                Type a question to ask {panelAgentLabel(agent)}
              </span>
            </span>
          ) : null}
        </div>
      </Command>
    </CommandDialog>
  );
};
